import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { getIdToken } from '../lib/auth';
import {
  getOwnProfile,
  createProfile,
  updateProfile,
  getPhotoUploadUrl,
  Profile,
  Address,
} from '../lib/api';
import { compressImageForVCard } from '../lib/imageUtils';
import { useProfileCompleteness } from '../hooks/useProfileCompleteness';
import { ImageCropModal } from '../components/photo';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import Footer from '../components/ui/Footer';
import QRCodeHero from '../components/dashboard/QRCodeHero';
import ProfileCompleteness from '../components/dashboard/ProfileCompleteness';
import LivePreviewCard from '../components/dashboard/LivePreviewCard';
import ContactPreviewCard from '../components/dashboard/ContactPreviewCard';
import StatsCard from '../components/dashboard/StatsCard';
import GlassCard from '../components/ui/GlassCard';
import { PhoneInput, CountrySelect } from '../components/phone';

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut: handleSignOut } = useAuth();
  const { applyProfileLanguage } = useLanguage();

  function onSignOut() {
    handleSignOut();
    navigate('/');
  }

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    title: 'Independent Consultant',
    tagline: '',
    phone: '',
    email: '',
    website: '',
    accentColor: '#e91e8c',
    address: {
      streetAddress: '',
      city: '',
      postalCode: '',
      country: '',
    } as Address,
  });

  const completeness = useProfileCompleteness(formData, profile);

  useEffect(() => {
    async function loadProfile() {
      try {
        const token = await getIdToken();
        if (token) {
          const existingProfile = await getOwnProfile(token);
          setProfile(existingProfile);
          setFormData({
            firstName: existingProfile.firstName,
            lastName: existingProfile.lastName,
            title: existingProfile.title,
            tagline: existingProfile.tagline || '',
            phone: existingProfile.phone || '',
            email: existingProfile.email || '',
            website: existingProfile.website || '',
            accentColor: existingProfile.accentColor || '#e91e8c',
            address: {
              streetAddress: existingProfile.address?.streetAddress || '',
              city: existingProfile.address?.city || '',
              postalCode: existingProfile.address?.postalCode || '',
              country: existingProfile.address?.country || '',
            },
          });
          applyProfileLanguage(existingProfile.preferredLanguage);
        }
      } catch {
        // No profile exists yet - prefill from Cognito user attributes
        if (user) {
          setFormData((prev) => ({
            ...prev,
            firstName: user.givenName || '',
            lastName: user.familyName || '',
            email: user.email || '',
          }));
        }
      } finally {
        setLoading(false);
      }
    }

    if (user) {
      loadProfile();
    }
  }, [user, applyProfileLanguage]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = await getIdToken();
      if (!token) {
        navigate('/login');
        return;
      }

      let result: Profile;
      if (profile) {
        result = await updateProfile(token, formData);
      } else {
        result = await createProfile(token, formData);
      }
      setProfile(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  function handlePhotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input value so onChange fires even if same file is selected again
    e.target.value = '';

    // Open crop modal with the selected file
    setSelectedImageFile(file);
    setCropModalOpen(true);
  }

  async function handleCropConfirm(croppedBlob: Blob) {
    try {
      const token = await getIdToken();
      if (!token) return;

      // Compress the cropped image for vCard compatibility (<30KB JPEG)
      const compressedBlob = await compressImageForVCard(
        new File([croppedBlob], 'photo.jpg', { type: 'image/jpeg' })
      );
      const contentType = 'image/jpeg';

      const { uploadUrl, photoUrl } = await getPhotoUploadUrl(token, contentType);

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: compressedBlob,
        headers: {
          'Content-Type': contentType,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status}`);
      }

      const updated = await updateProfile(token, { photoUrl });
      setProfile(updated);
    } catch {
      setError(t('dashboard.errors.uploadFailed'));
    }
  }

  function handleCropModalClose() {
    setCropModalOpen(false);
    setSelectedImageFile(null);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mk-pink-500 via-mk-pink-600 to-mk-pink-700">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  const profileUrl = profile
    ? `${window.location.origin}/p/${profile.publicId}`
    : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <DashboardHeader
        userName={profile ? `${profile.firstName} ${profile.lastName}` : undefined}
        userEmail={user?.email}
        photoUrl={profile?.photoUrl}
        firstName={profile?.firstName}
        lastName={profile?.lastName}
        accentColor={profile?.accentColor}
        onSignOut={onSignOut}
      />

      {/* Hero Section - Gradient Background */}
      {profile && profileUrl && (
        <section className="bg-gradient-to-r from-mk-pink-500 via-mk-pink-600 to-mk-pink-700 py-4 sm:py-5">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* QR Code Hero - takes 3/5 of space */}
              <div className="lg:col-span-3">
                <QRCodeHero profileUrl={profileUrl} firstName={formData.firstName} lastName={formData.lastName} />
              </div>

              {/* Profile Completeness - takes 2/5 of space */}
              <div className="lg:col-span-2">
                <ProfileCompleteness
                  percentage={completeness.percentage}
                  missingFields={completeness.missingFields}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <main className="flex-grow max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Form - spans 2 columns */}
          <div className="lg:col-span-2">
            <GlassCard className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                {profile ? t('dashboard.form.editTitle') : t('dashboard.form.createTitle')}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Photo Upload - shown at top of form */}
                {profile && (
                  <div className="flex items-center gap-4 pb-6 border-b border-gray-200">
                    {profile.photoUrl ? (
                      <img
                        key={profile.photoUrl}
                        src={profile.photoUrl}
                        alt={t('dashboard.photo.alt')}
                        className="w-20 h-20 rounded-full object-cover ring-4 ring-white shadow-lg"
                      />
                    ) : (
                      <div
                        className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold ring-4 ring-white shadow-lg"
                        style={{ backgroundColor: formData.accentColor }}
                      >
                        {profile.firstName[0]}
                        {profile.lastName[0]}
                      </div>
                    )}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        {t('dashboard.photo.title')}
                      </h3>
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <CameraIcon className="w-4 h-4 text-gray-500" />
                        {t('dashboard.photo.upload')}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoSelect}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                )}

                {/* Personal Information */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('dashboard.form.firstName')}
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({ ...formData, firstName: e.target.value })
                        }
                        className="w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent focus:bg-white transition-all duration-200"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('dashboard.form.lastName')}
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                        className="w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent focus:bg-white transition-all duration-200"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('dashboard.form.title')}
                  </label>
                  <div className="relative">
                    <BriefcaseIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      className="w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent focus:bg-white transition-all duration-200"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('dashboard.form.tagline')}
                  </label>
                  <div className="relative">
                    <SparklesIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.tagline}
                      onChange={(e) =>
                        setFormData({ ...formData, tagline: e.target.value })
                      }
                      placeholder={t('dashboard.form.taglinePlaceholder')}
                      className="w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent focus:bg-white transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Contact Information */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('dashboard.form.phone')}
                    </label>
                    <PhoneInput
                      value={formData.phone}
                      onChange={(phone) => setFormData({ ...formData, phone })}
                      defaultCountry="DE"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('dashboard.form.email')}
                    </label>
                    <div className="relative">
                      <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className="w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent focus:bg-white transition-all duration-200"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('dashboard.form.website')}
                  </label>
                  <div className="relative">
                    <GlobeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) =>
                        setFormData({ ...formData, website: e.target.value })
                      }
                      placeholder={t('dashboard.form.websitePlaceholder')}
                      className="w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent focus:bg-white transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Address Section */}
                <fieldset className="bg-gray-50/50 rounded-xl p-4 border border-gray-200">
                  <legend className="text-sm font-medium text-gray-700 px-2 flex items-center gap-2">
                    <MapPinIcon className="w-4 h-4 text-gray-400" />
                    {t('dashboard.form.address')}
                  </legend>
                  <div className="space-y-4 mt-2">
                    <div>
                      <label htmlFor="streetAddress" className="block text-sm text-gray-600 mb-1">
                        {t('dashboard.form.streetAddress')}
                      </label>
                      <input
                        id="streetAddress"
                        type="text"
                        value={formData.address.streetAddress}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            address: { ...formData.address, streetAddress: e.target.value },
                          })
                        }
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent transition-all duration-200"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="postalCode" className="block text-sm text-gray-600 mb-1">
                          {t('dashboard.form.postalCode')}
                        </label>
                        <input
                          id="postalCode"
                          type="text"
                          value={formData.address.postalCode}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              address: { ...formData.address, postalCode: e.target.value },
                            })
                          }
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label htmlFor="city" className="block text-sm text-gray-600 mb-1">
                          {t('dashboard.form.city')}
                        </label>
                        <input
                          id="city"
                          type="text"
                          value={formData.address.city}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              address: { ...formData.address, city: e.target.value },
                            })
                          }
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="country" className="block text-sm text-gray-600 mb-1">
                        {t('dashboard.form.country')}
                      </label>
                      <CountrySelect
                        value={formData.address.country || 'DE'}
                        onChange={(country) =>
                          setFormData({
                            ...formData,
                            address: { ...formData.address, country },
                          })
                        }
                        displayMode="name"
                        className="w-full"
                      />
                    </div>
                  </div>
                </fieldset>

                {/* Accent Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('dashboard.form.accentColor')}
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="color"
                      value={formData.accentColor}
                      onChange={(e) =>
                        setFormData({ ...formData, accentColor: e.target.value })
                      }
                      className="w-14 h-14 rounded-xl cursor-pointer border-2 border-gray-200 shadow-sm"
                    />
                    <div className="flex gap-2">
                      {['#e91e8c', '#ec4899', '#f43f5e', '#8b5cf6', '#3b82f6', '#10b981'].map((color) => (
                        <button
                          key={color}
                          type="button"
                          title={color}
                          aria-label={`Select color ${color}`}
                          onClick={() => setFormData({ ...formData, accentColor: color })}
                          className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                            formData.accentColor === color ? 'ring-2 ring-offset-2 ring-gray-400' : ''
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full py-4 bg-gradient-to-r from-mk-pink-500 to-mk-pink-600 text-white rounded-xl font-semibold hover:from-mk-pink-600 hover:to-mk-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving
                    ? t('dashboard.form.saving')
                    : profile
                    ? t('dashboard.form.updateButton')
                    : t('dashboard.form.createButton')}
                </button>
              </form>
            </GlassCard>
          </div>

          {/* Right Column - Preview & Stats */}
          <div className="space-y-6">
            {/* Live Preview */}
            <LivePreviewCard formData={formData} profile={profile} />

            {/* Contact Preview */}
            <ContactPreviewCard formData={formData} profile={profile} />

            {/* Stats Card */}
            <StatsCard
              stats={[
                { key: 'views', value: profile?.stats?.profileViews || 0, labelKey: 'views', icon: <EyeIcon className="w-5 h-5" /> },
                { key: 'vcards', value: profile?.stats?.vcardDownloads || 0, labelKey: 'vcards', icon: <DownloadIcon className="w-5 h-5" /> },
              ]}
              isPlaceholder={!profile?.stats}
            />
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer showCta={false} />

      {/* Image Crop Modal */}
      <ImageCropModal
        isOpen={cropModalOpen}
        onClose={handleCropModalClose}
        imageFile={selectedImageFile}
        onConfirm={handleCropConfirm}
      />
    </div>
  );
}

// Icon Components
function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function BriefcaseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function EnvelopeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}
